/* A COACH'S REMINDERS — the pure half (W82 step 6, the pro web; his rule (5), `coach_reminders`, live 2026-09-26).
 *
 * A reminder is the coach's own working note with a moment attached: "call Dana about the knee", optionally about one
 * client, due at an instant. The web lists them (the Dashboard card, the Reminders page, a client's `Remind me…`); the
 * coach's phone schedules a LOCAL notification per row (the phone is the clock — pg_cron is not installed).
 *
 * ⚠️ EVERY "WHEN" HERE IS ASKED OF THE COACH'S LOCAL CLOCK, THROUGH `days.ts` — never a second day rule. `now` and `zone`
 * are INJECTED on every function, because a function that reads the clock itself cannot be asked what it does at 00:30 in
 * Athens (21:30 UTC the day before), which is exactly the case a UTC day rule gets wrong.
 *
 * The buckets, and what each means on screen:
 *   • done      — `done_at` is set. Drawn in ink, struck through, folded on the Reminders page.
 *   • overdue   — not done and its moment has PASSED (`due_at` < now). Drawn in the warn colour.
 *   • today     — not done, still to come, on the coach's local today.
 *   • upcoming  — not done, on a later local day.
 * The Dashboard card lists overdue + today; the Reminders page lists everything, grouped by local day.
 */
import { localDayKey } from "./days";
import { longDate, shortDate } from "./weekLine";

export type Reminder = {
  id: string;
  clientId: string | null;
  title: string;
  /** `coach_reminders.due_at` — an instant (ISO). */
  dueAt: string;
  doneAt: string | null;
  createdAt: string;
};

export type ReminderBucket = "overdue" | "today" | "upcoming" | "done";

/** The table's CHECK: `char_length(btrim(title)) between 1 and 120`. */
export const REMINDER_TITLE_MAX = 120;

/** The row as PostgREST returns it — the ONE mapping from the table's spelling to ours. */
export type ReminderRow = { id: string; client_id: string | null; title: string; due_at: string; done_at: string | null; created_at: string };
export function fromRow(r: ReminderRow): Reminder {
  return { id: r.id, clientId: r.client_id, title: r.title, dueAt: r.due_at, doneAt: r.done_at, createdAt: r.created_at };
}

export function reminderBucket(r: Reminder, now: Date, zone: string): ReminderBucket {
  if (r.doneAt !== null) return "done";
  const due = Date.parse(r.dueAt);
  if (Number.isNaN(due)) return "upcoming";
  if (due < now.getTime()) return "overdue";
  return localDayKey(new Date(due), zone) === localDayKey(now, zone) ? "today" : "upcoming";
}

const byDue = (a: Reminder, b: Reminder) => Date.parse(a.dueAt) - Date.parse(b.dueAt) || a.id.localeCompare(b.id);

/** THE DASHBOARD CARD: overdue first (the oldest first — the longest-waiting), then today's still to come, soonest first. */
export function dashboardReminders(rows: readonly Reminder[], now: Date, zone: string): Reminder[] {
  const overdue = rows.filter((r) => reminderBucket(r, now, zone) === "overdue").sort(byDue);
  const today = rows.filter((r) => reminderBucket(r, now, zone) === "today").sort(byDue);
  return [...overdue, ...today];
}

/** The card's head: `2 overdue · 1 today`, or null when there is nothing to say. */
export function dashboardCountLine(rows: readonly Reminder[], now: Date, zone: string): string | null {
  const o = rows.filter((r) => reminderBucket(r, now, zone) === "overdue").length;
  const t = rows.filter((r) => reminderBucket(r, now, zone) === "today").length;
  const parts = [o > 0 ? `${o} overdue` : null, t > 0 ? `${t} today` : null].filter((p): p is string => p !== null);
  return parts.length === 0 ? null : parts.join(" · ");
}

export const REMINDERS_EMPTY = "Nothing due today.";
export const REMINDERS_PAGE_EMPTY = "No reminders yet.";

/** The label of a local day relative to today: Today · Tomorrow · Yesterday · `Thu 24 Sep` (`Mon 4 Jan 2027` in another year). */
export function dayLabel(dayKey: string, now: Date, zone: string): string {
  const today = localDayKey(now, zone);
  const diff = Math.round((Date.parse(`${dayKey}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  // The web's ONE date vocabulary (`weekLine.ts`, dateVocabulary.test.ts) — never a second month table.
  return dayKey.slice(0, 4) === today.slice(0, 4) ? shortDate(dayKey) : longDate(dayKey);
}

/**
 * W101 — the Dashboard's WHEN, one word (his *"only the mandatory info"*): an overdue or today's reminder says `today`
 * (the overdue one in the accent — the card's CSS), then `tomorrow`, a weekday (`Fri`) within the week, and the page's short
 * date beyond it. The clock time stays on the Reminders page, where the day is a group heading.
 */
export function reminderDayWord(r: Reminder, now: Date, zone: string): string {
  const bucket = reminderBucket(r, now, zone);
  if (bucket === "overdue" || bucket === "today") return "today";
  const day = localDayKey(new Date(r.dueAt), zone);
  const diff = Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${localDayKey(now, zone)}T00:00:00Z`)) / 86_400_000);
  if (diff === 1) return "tomorrow";
  const label = dayLabel(day, now, zone);
  return diff > 1 && diff < 7 ? (label.split(" ")[0] ?? label) : label;
}

/** `HH:MM` of an instant on the coach's clock (24-hour, the phone's own format). */
export function clockOf(iso: string, zone: string): string {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(t);
    const get = (k: string) => parts.find((p) => p.type === k)?.value ?? "";
    return `${get("hour")}:${get("minute")}`;
  } catch {
    return t.toISOString().slice(11, 16);
  }
}

/** When, in words, for a row outside a day group (the Dashboard card): `09:00` today, `Yesterday 18:30`, `Thu 24 Sep 09:00`. */
export function whenWords(r: Reminder, now: Date, zone: string): string {
  const day = localDayKey(new Date(r.dueAt), zone);
  const time = clockOf(r.dueAt, zone);
  return day === localDayKey(now, zone) ? time : `${dayLabel(day, now, zone)} ${time}`;
}

export type ReminderGroup = { key: string; label: string; rows: Reminder[] };

/** THE REMINDERS PAGE: every open reminder grouped by its LOCAL day (earliest day first, each by time), and the done
 *  ones apart — the page folds them, newest done first. */
export function groupReminders(rows: readonly Reminder[], now: Date, zone: string): { open: ReminderGroup[]; done: Reminder[] } {
  const open = rows.filter((r) => r.doneAt === null).sort(byDue);
  const groups: ReminderGroup[] = [];
  for (const r of open) {
    const key = localDayKey(new Date(r.dueAt), zone);
    const last = groups[groups.length - 1];
    if (last !== undefined && last.key === key) last.rows.push(r);
    else groups.push({ key, label: dayLabel(key, now, zone), rows: [r] });
  }
  const done = rows.filter((r) => r.doneAt !== null)
    .sort((a, b) => Date.parse(b.doneAt ?? "") - Date.parse(a.doneAt ?? "") || a.id.localeCompare(b.id));
  return { open: groups, done };
}

/** The fold's words: `3 done ›` / `Hide done`. Null when nothing is done. */
export function doneFoldLabel(n: number, shown: boolean): string | null {
  if (n === 0) return null;
  return shown ? "Hide done" : `${n} done ›`;
}

/* ── The draft (the `Remind me…` window) ───────────────────────────────────────────────────────────────────── */

/** The instant a wall-clock time on a local day names in `zone` — `2026-09-27` + `09:00` in Athens → `06:00Z`. Null when
 *  the parts do not parse. ⚠️ Two passes, because the zone's offset at the GUESS may differ from the offset at the answer
 *  across a daylight-saving change. */
export function localInstant(dayKey: string, hhmm: string, zone: string): string | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey); const t = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (d === null || t === null) return null;
  const [y, mo, da, h, mi] = [d[1], d[2], d[3], t[1], t[2]].map(Number) as [number, number, number, number, number];
  if (mo < 1 || mo > 12 || da < 1 || da > 31 || h > 23 || mi > 59) return null;
  const wall = Date.UTC(y, mo - 1, da, h, mi);
  const offsetAt = (ms: number) => {
    try {
      const parts = new Intl.DateTimeFormat("en-GB", { timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
        .formatToParts(new Date(ms));
      const g = (k: string) => Number(parts.find((p) => p.type === k)?.value ?? "0");
      return Date.UTC(g("year"), g("month") - 1, g("day"), g("hour"), g("minute")) - ms;
    } catch { return 0; }
  };
  let at = wall - offsetAt(wall);
  at = wall - offsetAt(at);
  return new Date(at).toISOString();
}

/** The day and time an instant shows on the coach's clock — what the window pre-fills when editing. */
export function localParts(iso: string, zone: string): { day: string; time: string } {
  return { day: localDayKey(new Date(iso), zone), time: clockOf(iso, zone) };
}

/** A new reminder's default moment: the next whole hour today, or 09:00 tomorrow once it is 21:00 or later. */
export function defaultWhen(now: Date, zone: string): { day: string; time: string } {
  const here = localParts(now.toISOString(), zone);
  const hour = Number(here.time.slice(0, 2));
  if (hour >= 21) {
    const tomorrow = new Date(Date.parse(`${here.day}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
    return { day: tomorrow, time: "09:00" };
  }
  return { day: here.day, time: `${String(hour + 1).padStart(2, "0")}:00` };
}

export type ReminderDraft = { title: string; day: string; time: string; clientId: string | null };

/** What stops a draft saving, in words — or null. The title rule is the table's CHECK, said before the database says it. */
export function draftFault(d: ReminderDraft, zone: string): string | null {
  const t = d.title.trim();
  if (t === "") return "Give the reminder a title.";
  if (t.length > REMINDER_TITLE_MAX) return `A title is ${REMINDER_TITLE_MAX} characters at most.`;
  if (localInstant(d.day, d.time, zone) === null) return "Choose a day and a time.";
  return null;
}

/** The row a draft writes — the ONE shape every insert and edit sends (`coach_id` is the column default, `auth.uid()`). */
export function draftRow(d: ReminderDraft, zone: string): { title: string; due_at: string; client_id: string | null } | null {
  if (draftFault(d, zone) !== null) return null;
  return { title: d.title.trim(), due_at: localInstant(d.day, d.time, zone) as string, client_id: d.clientId };
}
